import Employee from 'models/employee';
import { ColumnDef, Row } from '@tanstack/react-table';
import IconButton from 'Components/atoms/buttons/icon-button';
import TableRowControlsContainer from 'Components/atoms/containers/table-row-controls-container';
import { EMPLOYEE_TYPES, EMPLOYEE_TYPE_VARIANT } from 'models/employee-type';
import { EMPLOYEE_STATUS_VARIANT } from 'models/employment-status';
import { PencilIcon, TrashIcon } from 'Components/atoms/icons';
import Chip from 'Components/atoms/chip';

interface EmployeeBaseColumnsProps {
    onCodeClick?: (employee: Employee) => void;
    onEdit?: (employee: Employee) => void;
    onDelete?: (employee: Employee) => void;
}

export const employeeBaseColumns = ({
    onCodeClick,
    onEdit,
    onDelete,
}: EmployeeBaseColumnsProps): ColumnDef<Employee>[] => {

    const columns: ColumnDef<Employee>[] = [
        {
            header: "Code",
            accessorKey: "employeeCode",
            size: 120,
            cell: ({ row }) => {
                const isConsultant = row.original.employeeType?.name === EMPLOYEE_TYPES.CONSULTANT || row.original.employeeType?.name === EMPLOYEE_TYPES.POTENTIAL_CONSULTANT
                return <TableRowControlsContainer>
                    {onCodeClick
                        ? <a
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                color: "var(--bs-table-link-color)",
                                cursor: "pointer",
                            }}
                            onClick={() => onCodeClick(row.original)}
                        >
                            {row.original.employeeCode || ""}
                        </a>
                        : row.original.employeeCode || ""}
                    <div className='d-flex flex-row justify-content-end'>
                        {(isConsultant && onEdit) ? <IconButton title="Edit" onClick={() => onEdit(row.original)}><PencilIcon /></IconButton> : undefined}
                        {(isConsultant && onDelete) ? <IconButton title="Delete" onClick={() => onDelete(row.original)}><TrashIcon /></IconButton> : undefined}
                    </div>
                </TableRowControlsContainer>
            }
        },
        {
            header: "Name",
            id: "name",
            size: 150,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) =>
                `${row.original.commonName ?? row.original.firstName} ${row.original.lastName}`,
            accessorFn: (row) => `${row.commonName ?? row.firstName} ${row.lastName}`.trim(),
        },
        {
            header: "Phone Number",
            id: "phoneNumber",
            size: 150,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.phoneNumber
                ? <a href={`tel:${row.original.phoneNumber.replace(/[^0-9+]/g, '')}`}>{row.original.phoneNumber}</a>
                : "Not found",
            accessorFn: (row) => row.phoneNumber?.trim(),
        },
        {
            header: "Email",
            id: "email",
            size: 220,
            cell: ({ row }) => {
                const email = row.original.companyEmail ?? row.original.personalEmail
                return email ? <a href={`mailto:${email}`}>{email}</a> : "Not found"
            },
            accessorFn: (row) => row.companyEmail ?? row.personalEmail ?? undefined,
        },
        {
            header: "Employee Type",
            id: "employeeType",
            size: 150,

            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => {
                const type = row.original.employeeType?.name
                if (!type) return "Not found"

                return <Chip variant={EMPLOYEE_TYPE_VARIANT[type]}>{type}</Chip>
            },
            accessorFn: (row) => row.employeeType?.name,
        },
        {
            header: "Job Title",
            id: "jobTitle",
            size: 200,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.jobTitle?.description ?? "N/A",
            accessorFn: (row) => row.jobTitle?.description,
            filterFn: (row: Row<Employee>, columnId: string, filterValue: any) =>
                row.original.jobTitle?.description === filterValue,
        },
        {
            header: "Department",
            id: "department",
            size: 175,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.department?.description ?? "N/A",
            accessorFn: (row) => row.department?.description,
            filterFn: (row: Row<Employee>, columnId: string, filterValue: any) =>
                row.original.department?.description === filterValue,
        },
        {
            header: "Province",
            id: "province",
            size: 100,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.location?.address?.region?.name ?? "N/A",
            accessorFn: (row) => row.location?.address?.region?.name,
            filterFn: (row: Row<Employee>, columnId: string, filterValue: any) =>
                row.original.location?.address?.region?.name === filterValue,
        },
        {
            header: "Manager",
            id: "manager",
            size: 175,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.manager
                ? `${row.original.manager.firstName} ${row.original.manager.lastName}`
                : "N/A",
            accessorFn: (row) => row.manager ? `${row.manager.firstName} ${row.manager.lastName}` : undefined,
        },
        {
            header: "Start Date",
            id: "hireDate",
            size: 100,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.hireDate?.toString().split('T')[0] ?? "N/A",
            accessorFn: (row) => row.hireDate?.toString(),
        },
        {
            header: "Contract End Date",
            id: "contractEndDate",
            size: 130,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => row.original.contractEndDate?.toString().split('T')[0] ?? "N/A",
            accessorFn: (row) => row.contractEndDate?.toString(),
        },
        {
            header: "Status",
            id: "status",
            size: 90,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) => {
                const status = row.original.employeeStatus?.description
                if (!status) return "N/A"

                return <Chip variant={EMPLOYEE_STATUS_VARIANT[status]}>{status}</Chip>
            },
            accessorFn: (row) => row.employeeStatus?.description ?? "N/A",
            filterFn: (row, columnId, filterValue) =>
                row.original.employeeStatus?.description === filterValue,
            enableHiding: true,
        },
        {
            header: "WTR",
            id: "willingToRelocate",
            size: 80,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            accessorFn: (row) => row.willingToRelocate ? "Yes" : "No",
            cell: ({ row }) =>
                <Chip variant={row.original.willingToRelocate ? 'success' : 'danger'}>
                    {row.original.willingToRelocate ? "Yes" : "No"}
                </Chip>,

        }
    ];

    return columns;
};